import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { districtForPath } from "./districts";
import { transitionOverlayScene, travelVisualForPath } from "./visualPack";

type TravelPhase = "idle" | "departing" | "arriving";
type TravelState = {
  phase: TravelPhase;
  fromPath: string;
  toPath: string;
};

const DEPARTURE_MS = 620;
const ARRIVAL_MS = 900;

function scene(pathname: string) {
  return travelVisualForPath(pathname, districtForPath(pathname));
}

export function WorldTravelLayer() {
  const location = useLocation();
  const navigate = useNavigate();
  const timers = useRef<number[]>([]);
  const travelling = useRef(false);
  const [state, setState] = useState<TravelState>({
    phase: "idle",
    fromPath: location.pathname,
    toPath: location.pathname,
  });

  const clearTimers = () => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current = [];
  };

  useEffect(() => {
    document.documentElement.classList.add("world-route-arrival");
    const timer = window.setTimeout(
      () => document.documentElement.classList.remove("world-route-arrival"),
      ARRIVAL_MS,
    );
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      if (
        !anchor ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download") ||
        anchor.dataset.noWorldTravel === "true"
      ) {
        return;
      }

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const current = new URL(window.location.href);
      if (
        current.searchParams.has("visualPack") &&
        !url.searchParams.has("visualPack")
      ) {
        url.searchParams.set(
          "visualPack",
          current.searchParams.get("visualPack") ?? "",
        );
      }
      if (url.pathname === current.pathname && url.search === current.search) {
        return;
      }

      if (travelling.current) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      const destination = `${url.pathname}${url.search}${url.hash}`;
      if (reducedMotion) {
        navigate(destination);
        return;
      }

      clearTimers();
      travelling.current = true;
      document.documentElement.dataset.worldTravel = "departing";
      setState({
        phase: "departing",
        fromPath: current.pathname,
        toPath: url.pathname,
      });

      timers.current.push(
        window.setTimeout(() => {
          navigate(destination);
          document.documentElement.dataset.worldTravel = "arriving";
          setState((value) => ({ ...value, phase: "arriving" }));
        }, DEPARTURE_MS),
      );
      timers.current.push(
        window.setTimeout(() => {
          travelling.current = false;
          delete document.documentElement.dataset.worldTravel;
          setState((value) => ({
            ...value,
            phase: "idle",
            fromPath: value.toPath,
          }));
        }, DEPARTURE_MS + ARRIVAL_MS),
      );
    };

    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      clearTimers();
      travelling.current = false;
      delete document.documentElement.dataset.worldTravel;
    };
  }, [navigate]);

  const from = districtForPath(state.fromPath);
  const to = districtForPath(state.toPath);
  const fromScene = scene(state.fromPath);
  const toScene = scene(state.toPath);
  const overlay = transitionOverlayScene();
  const active = state.phase !== "idle";

  return (
    <div
      className={`world-travel-layer ${state.phase}`}
      data-phase={state.phase}
      data-visual-pack={toScene.packId}
      aria-hidden="true"
    >
      {active ? (
        <>
          <div
            className="travel-scene travel-scene-from"
            data-visual-asset={fromScene.assetId}
            style={{ backgroundImage: `url(${fromScene.desktopUrl})` }}
          />
          <div
            className="travel-scene travel-scene-to"
            data-visual-asset={toScene.assetId}
            style={{ backgroundImage: `url(${toScene.desktopUrl})` }}
          />
          {overlay ? (
            <div
              className="travel-visual-pack-overlay"
              data-visual-asset={overlay.assetId}
              style={{ backgroundImage: `url(${overlay.desktopUrl})` }}
            />
          ) : null}
          <div className="travel-speed-lines" />
          <div className="travel-cloud-gate travel-cloud-gate-one" />
          <div className="travel-cloud-gate travel-cloud-gate-two" />
          <div className="travel-compass">
            <span>
              {state.phase === "departing"
                ? "Travelling through the village"
                : "Arriving"}
            </span>
            <strong>
              {state.phase === "departing"
                ? from.districtLabel
                : to.districtLabel}
            </strong>
            <i />
            <small>{to.districtLabel}</small>
          </div>
        </>
      ) : null}
    </div>
  );
}
